import { FunnelSimple } from "@phosphor-icons/react";

import RenderIf from "common/components/RenderIf";
import {
  DropdownMenu,
  DropdownMenuCheckbox,
  DropdownMenuContent,
  DropdownMenuCount,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import FilterButton from "common/components/ui/FilterButton";
import { typedObjectKeys } from "common/helpers/utils";

import {
  AUDIENCE_PROFILE_STATES,
  CANCELLED_TARGET_STATES,
} from "../../../../constants";

import type {
  AudienceProfile,
  AudienceProfileState,
  CancelledTarget,
  CancelledTargetState,
} from "../../../../types";

interface FiltersPopoverProps {
  selectedStates: (AudienceProfileState | CancelledTargetState)[];
  setSelectedStates: (
    selectedGroups: (AudienceProfileState | CancelledTargetState)[],
  ) => void;
  audienceWithCancellation: (AudienceProfile & {
    cancellationDetails: CancelledTarget;
  })[];
}

export default function FiltersPopover({
  selectedStates,
  setSelectedStates,
  audienceWithCancellation,
}: FiltersPopoverProps) {
  function addStateFilters(
    states: (AudienceProfileState | CancelledTargetState)[],
  ) {
    setSelectedStates([...selectedStates, ...states]);
  }
  function removeStateFilters(
    states: (AudienceProfileState | CancelledTargetState)[],
  ) {
    setSelectedStates(
      selectedStates.filter((state) => !states.includes(state)),
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <FilterButton leftIcon={<FunnelSimple />} className="w-48">
          {selectedStates.length
            ? `${selectedStates.length} ${selectedStates.length === 1 ? "filter" : "filters"}`
            : "All audience"}
        </FilterButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuList>
          <DropdownMenuItem onSelect={() => setSelectedStates([])}>
            All audience
            {audienceWithCancellation && (
              <DropdownMenuCount>
                {audienceWithCancellation.length}
              </DropdownMenuCount>
            )}
          </DropdownMenuItem>
          {AUDIENCE_PROFILE_STATES.map(({ label, states }) => {
            const isCancelledState = states.includes("cancelled");
            const allStates = isCancelledState
              ? [...states, ...typedObjectKeys(CANCELLED_TARGET_STATES)]
              : states;
            const isChecked = selectedStates.some((selectedState) =>
              allStates.some((state) => selectedState === state),
            );

            return (
              <>
                <DropdownMenuItem
                  key={label}
                  onSelect={() => {
                    setSelectedStates(allStates);
                  }}
                >
                  <DropdownMenuCheckbox
                    isChecked={
                      isCancelledState &&
                      isChecked &&
                      !typedObjectKeys(CANCELLED_TARGET_STATES).every((state) =>
                        selectedStates.includes(state),
                      )
                        ? "indeterminate"
                        : isChecked
                    }
                    onCheckedChange={() => {
                      if (isChecked) {
                        removeStateFilters(allStates);
                      } else {
                        addStateFilters(allStates);
                      }
                    }}
                  />
                  {label}
                  {audienceWithCancellation && (
                    <DropdownMenuCount>
                      {
                        audienceWithCancellation.filter(({ state }) =>
                          states.includes(state),
                        ).length
                      }
                    </DropdownMenuCount>
                  )}
                </DropdownMenuItem>
                <RenderIf condition={isCancelledState}>
                  {(
                    Object.entries(CANCELLED_TARGET_STATES) as [
                      CancelledTargetState,
                      string,
                    ][]
                  ).map(([subState, label]) => {
                    const isSubStateChecked = selectedStates.includes(subState);

                    return (
                      <DropdownMenuItem
                        key={subState}
                        isSubItem
                        onSelect={() => {
                          setSelectedStates([subState]);
                        }}
                      >
                        <DropdownMenuCheckbox
                          isChecked={isSubStateChecked}
                          onCheckedChange={() => {
                            if (isSubStateChecked) {
                              removeStateFilters([subState]);
                            } else {
                              addStateFilters([subState]);
                            }
                          }}
                        />
                        {label}
                        {audienceWithCancellation && (
                          <DropdownMenuCount>
                            {
                              audienceWithCancellation.filter(
                                ({ cancellationDetails }) =>
                                  cancellationDetails?.transition === subState,
                              ).length
                            }
                          </DropdownMenuCount>
                        )}
                      </DropdownMenuItem>
                    );
                  })}
                </RenderIf>
              </>
            );
          })}
        </DropdownMenuList>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
